import db, { getLastSourceCode, setLastSourceCode } from "../db/database";
import { fetchPagedFromBusinessCentral } from "../utils/businessCentralUtils";
import { marcaAGuardarConSolape } from "./margenDeSolape";
import {
  anotarAviso,
  anotarError,
  anotarLectura,
  anotarPaso,
  anotarSinConexion,
  anotarSinNovedades,
} from "./syncLog";

/**
 * Propagación de las bajas hechas EN Business Central.
 *
 * Es el acuerdo 8, y era el único punto abierto con consecuencia sobre la
 * corrección de los datos: un registro borrado en BC **nunca desaparecía del
 * terminal**. No es un descuido, es estructural — un registro borrado no puede
 * llevar contador de cambios, así que la lectura incremental no lo ve jamás.
 *
 * BC publica ahora una tabla que anota cada baja, con un entero autoincremental
 * que sirve de cursor. El terminal la lee como cualquier otro catálogo
 * incremental --`Source_Counter gt N`-- y por cada fila borra el registro local
 * que case. Es idempotente: si ya no está, no hay nada que hacer.
 *
 * Dos cosas que conviene saber antes de ver la consola:
 *
 * - **El terminal lee sus propias bajas de vuelta.** Cuando el TPV manda una
 *   baja a BC, el suscriptor de allá también la anota. No molesta --el registro
 *   ya no está en local-- pero aparece como "ya no estaba".
 * - **Un terminal sin marca previa lee desde el principio.** La primera versión
 *   saltaba al máximo actual para no reproducir el histórico, y ese atajo se
 *   comió la primera baja real que hubo: la lista nace vacía, así que todo lo
 *   que contiene es posterior a los datos que el terminal ya tiene y no hay
 *   histórico del que protegerse. Reproducirla es inocuo — una baja de algo que
 *   no está en local no hace nada.
 */

const ENTIDAD = "bajas de Business Central";

// La conexión se mira directo y no a través de syncService a propósito: esto lo
// llama ahora la barra de cualquier listado, y depender de syncService --que a
// su vez importa este archivo-- deja un ciclo de módulos que no aporta nada.
// isOnline() allá es exactamente esto.
const hayConexion = () => navigator.onLine;

// Una sola lectura a la vez.
//
// La lista es global --una fila puede ser de cualquier maestro-- y ahora se
// pide desde varios sitios: la sincronización completa, la de un modelo suelto
// y el botón de cada listado. Sin esto, dos llamadas simultáneas leerían el
// mismo bloque y aplicarían las mismas bajas dos veces; con esto comparten la
// misma lectura y el segundo obtiene el resultado del primero.
let lecturaEnCurso = null;

// Clave con la que se guarda el cursor en syncMetadata. No se llama igual que la
// entidad de la consola a propósito: esta es la interna.
//
// El sufijo no es decorativo. La versión anterior guardaba acá el máximo actual
// en la primera lectura, sin aplicar nada, y eso dejó una baja real fuera de
// alcance para siempre: el filtro es `gt`, así que una marca adelantada no se
// puede deshacer. Cambiar la clave descarta las marcas calculadas con aquella
// regla — los terminales que ya la tenían vuelven a leer desde el principio, una
// sola vez.
const CHECKPOINT = "bajasBC-desde-cero";

const ENDPOINT = "ListaBajas";

const TEXTO = "texto";
const NUMERO = "numero";
const FECHA = "fecha";

/**
 * Qué borra cada aviso de baja, y con qué claves se localiza el registro local.
 *
 * Business Central manda la clave **tal como la conoce el TPV**, que no siempre
 * es su clave primaria: en las aperturas de caja manda el identificador de
 * replicación y no la terna (tienda, caja, fecha).
 *
 * Los nombres de campo de la derecha son los de la base local, que no coinciden
 * con los de BC — `CustomerNo` acá es `Customer No.` allá.
 */
export const MAESTROS_CON_BAJA = {
  CLIENTE: { tabla: "clients", claves: [["No", TEXTO]] },
  DIRECCION: {
    tabla: "clientAddresses",
    claves: [
      ["Code", TEXTO],
      ["CustomerNo", TEXTO],
    ],
  },
  COLEGIO: { tabla: "colegios", claves: [["No", TEXTO]] },
  TIENDA: { tabla: "tpvs", claves: [["No", TEXTO]] },
  CAJA: {
    tabla: "cajas",
    claves: [
      ["No", TEXTO],
      ["TPV", TEXTO],
    ],
  },
  USUARIO: {
    tabla: "users",
    claves: [
      ["No", TEXTO],
      ["Tienda", TEXTO],
    ],
  },
  TURNO: { tabla: "turnos", claves: [["No", TEXTO]] },
  APERTURA: { tabla: "aperturaDeCaja", claves: [["No", TEXTO]] },
  PRODUCTO: { tabla: "products", claves: [["No", TEXTO]] },
  // Las dos que BC no listó en su resumen pero sí anota en el código. Se cubren
  // igual: si llegaran y no estuvieran acá, la fila se descartaría por entidad
  // desconocida y la baja no se aplicaría nunca.
  KIT: {
    tabla: "productLines",
    claves: [
      ["Parent_Item_No", TEXTO],
      ["Line_No", NUMERO],
    ],
  },
  PRECIO: {
    tabla: "salesPrices",
    claves: [
      ["Item_No", TEXTO],
      ["Sales_Code", TEXTO],
      ["Starting_Date", FECHA],
    ],
  },
};

const normalizarTexto = (valor) => String(valor ?? "").trim().toUpperCase();

// Sólo la parte de fecha. BC formatea la suya con el idioma de la sesión, así
// que la comparación va por valor y no por texto -- el mismo cuidado que ya hizo
// falta con el identificador "TIENDA;CAJA;FECHA".
const normalizarFecha = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;

  const texto = String(valor).trim();

  // ISO o "YYYY-MM-DD..." se toma tal cual, sin pasar por Date: construir una
  // fecha aplica el huso horario y puede correrla un día.
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dmy = texto.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmy) {
    const [, dia, mes, anio] = dmy;
    const anioCompleto = anio.length === 2 ? `20${anio}` : anio;
    return `${anioCompleto}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
  }

  const fecha = new Date(texto);
  return Number.isNaN(fecha.getTime()) ? null : fecha.toISOString().slice(0, 10);
};

const mismoValor = (tipo, esperado, local) => {
  if (tipo === NUMERO) {
    const a = Number(esperado);
    const b = Number(local);
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
  }

  if (tipo === FECHA) {
    const a = normalizarFecha(esperado);
    const b = normalizarFecha(local);
    // Una fecha que no se puede interpretar NO coincide con nada. Es la
    // dirección segura: dejar una baja sin aplicar se ve y se corrige; borrar de
    // más no se recupera.
    return a !== null && a === b;
  }

  return normalizarTexto(esperado) === normalizarTexto(local);
};

/** Las claves que la entidad declara tienen que venir todas informadas. */
export const clavesCompletas = (definicion, fila) =>
  definicion.claves.every((_, indice) => {
    const valor = fila?.[`Clave${indice + 1}`];
    return valor !== null && valor !== undefined && String(valor).trim() !== "";
  });

/**
 * Si un registro local es el que Business Central borró.
 *
 * Exportada para poder probarla: es la decisión con consecuencias, y equivocarse
 * acá borra datos que nadie pidió borrar.
 */
export const coincide = (definicion, fila, registro) =>
  definicion.claves.every(([campo, tipo], indice) =>
    mismoValor(tipo, fila?.[`Clave${indice + 1}`], registro?.[campo])
  );

/**
 * Aplica una fila del aviso de baja.
 *
 * @returns {Promise<{estado: string, borrados?: number, conCambios?: number}>}
 */
const aplicarBaja = async (fila) => {
  const definicion = MAESTROS_CON_BAJA[normalizarTexto(fila?.Entidad)];

  if (!definicion) return { estado: "desconocida" };
  if (!clavesCompletas(definicion, fila)) return { estado: "incompleta" };

  const candidatos = await db[definicion.tabla]
    .filter((registro) => coincide(definicion, fila, registro))
    .toArray();

  if (candidatos.length === 0) return { estado: "ausente" };

  // Un registro con cambios locales sin enviar se borra igual --en BC ya no
  // existe, así que enviarlos fallaría-- pero se cuenta aparte para que no
  // desaparezca en silencio.
  const conCambios = candidatos.filter(
    (registro) => registro.pendingSync === 1 && !registro.deleted
  ).length;

  await db[definicion.tabla].bulkDelete(candidatos.map((registro) => registro.id));

  return { estado: "aplicada", borrados: candidatos.length, conCambios };
};

/**
 * Lee los avisos de baja pendientes y los aplica sobre la base local.
 *
 * No aborta la sincronización si falla: es una reconciliación, no un paso del
 * que dependa lo demás. Si el servicio web no está publicado en el entorno --hoy
 * pasa en uno de los dos-- se anota y se sigue.
 */
export const pullBajasDesdeBC = async () => {
  if (lecturaEnCurso) return lecturaEnCurso;

  lecturaEnCurso = leerYAplicarBajas().finally(() => {
    lecturaEnCurso = null;
  });

  return lecturaEnCurso;
};

const leerYAplicarBajas = async () => {
  if (!hayConexion()) {
    anotarSinConexion(ENTIDAD);
    return { ok: false, aplicadas: 0 };
  }

  try {
    const checkpoint = await getLastSourceCode(CHECKPOINT);

    // Sin marca previa se lee desde el principio, no desde el máximo actual.
    //
    // El atajo de arrancar en el máximo parecía prudente --evitaba reproducir el
    // histórico-- pero era destructivo: se saltaba avisos que nadie había
    // aplicado y, como el filtro es `gt`, no había forma de volver a verlos.
    // Con la lista recién creada eso se comió la única baja que existía.
    const desde = checkpoint > 0 ? checkpoint : 0;

    if (desde === 0) {
      anotarPaso(ENTIDAD, "primera lectura · se lee la lista completa desde el principio");
    }

    const resumen = { aplicada: 0, ausente: 0, desconocida: 0, incompleta: 0 };
    let borrados = 0;
    let conCambios = 0;
    let marca = desde;
    // Todos los contadores del lote, para poder guardar el K-ésimo y no el
    // mayor: así la lectura siguiente vuelve a pasar por esos avisos. Ver
    // `margenDeSolape.js`. Reaplicar una baja no cuesta: la fila ya no está.
    const contadoresDelLote = [];

    const leidas = await fetchPagedFromBusinessCentral(ENDPOINT, {
      keyField: "Source_Counter",
      filter: `Source_Counter gt ${desde}`,
      onPage: async (pagina) => {
        for (const fila of pagina) {
          const resultado = await aplicarBaja(fila);
          resumen[resultado.estado] = (resumen[resultado.estado] || 0) + 1;
          borrados += resultado.borrados || 0;
          conCambios += resultado.conCambios || 0;

          const cursor = Number(fila?.Source_Counter);
          if (Number.isFinite(cursor)) {
            contadoresDelLote.push(cursor);
            if (cursor > marca) marca = cursor;
          }

          if (resultado.estado === "desconocida") {
            anotarAviso(ENTIDAD, `entidad no reconocida: "${fila?.Entidad}" (se ignora)`);
          }
          if (resultado.estado === "incompleta") {
            anotarAviso(
              ENTIDAD,
              `aviso de ${fila?.Entidad} sin todas sus claves (se ignora, para no borrar de más)`
            );
          }
        }
      },
    });

    if (leidas === 0) {
      anotarSinNovedades(ENTIDAD, true);
      return { ok: true, aplicadas: 0, marca: desde };
    }

    // La marca sólo avanza si de verdad avanzó: guardar una mayor dejaría avisos
    // sin aplicar para siempre, que es justo el defecto que esto viene a cerrar.
    const marcaAGuardar = marcaAGuardarConSolape(contadoresDelLote, desde);
    if (marcaAGuardar > desde) await setLastSourceCode(marcaAGuardar, CHECKPOINT);

    anotarLectura(ENTIDAD, { guardados: leidas, incremental: true, marca });
    anotarPaso(
      ENTIDAD,
      `${resumen.aplicada} aplicadas (${borrados} registros) · ${resumen.ausente} ya no estaban`
    );

    if (conCambios > 0) {
      anotarAviso(
        ENTIDAD,
        `${conCambios} de los borrados tenían cambios locales sin enviar, que se pierden — en Business Central ya no existen`
      );
    }

    return { ok: true, aplicadas: resumen.aplicada, borrados, conCambios, marca };
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return { ok: false, aplicadas: 0, message: error?.message };
  }
};
